import React, { useState } from "react";
import { toast } from "sonner";
import { FilePdf, Printer, ArrowsClockwise, MagnifyingGlassPlus, SealWarning } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { tokenHeader, track } from "../../lib/clarity";
import ScoreDial from "./ScoreDial";
import ReportCategories from "./ReportCategories";
import ReportTaxRetire from "./ReportTaxRetire";
import ReportNextSteps, { ActionPlan } from "./ReportNextSteps";
import Disclosure from "./Disclosure";

export default function ClarityReport({ data, token, onDeep, onRetake, onNeedContact }) {
  const r = data.result;
  const [dl, setDl] = useState(false);

  const pdf = async () => {
    if (!data.contact_captured) return onNeedContact();
    setDl(true);
    try {
      const res = await api.get(`/clarity/${data.assessment_id}/pdf`, { ...tokenHeader(token), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = Object.assign(document.createElement("a"), { href: url, download: "exodus-clarity-report.pdf" });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      track("pdf_download");
    } catch { toast.error("Could not generate the PDF."); } finally { setDl(false); }
  };

  return (
    <div data-testid="clarity-report" className="space-y-10 print-area">
      <section className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-4 flex justify-center lg:justify-start"><ScoreDial score={r.overall} size={240} /></div>
        <div className="lg:col-span-8 space-y-4">
          <p className="eyebrow">{data.first_name ? `${data.first_name}'s` : "Your"} Financial Clarity Score</p>
          <h1 data-testid="report-band" className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1]">{r.band}</h1>
          <p className="text-exodus-muted max-w-2xl">{r.band_description}</p>
          {data.narrative?.summary && <p data-testid="report-narrative" className="text-exodus-text/90 max-w-2xl leading-relaxed">{data.narrative.summary}</p>}
          <div className="flex flex-wrap gap-3 pt-2">
            <span data-testid="report-confidence" className="text-xs border border-exodus-border px-3 py-1.5 text-exodus-muted">Confidence: <span className="text-exodus-text">{r.confidence_label}</span> · {Math.round(r.confidence * 100)}% answered</span>
            {r.provisional && <span data-testid="report-provisional" className="text-xs border border-orange-300/50 px-3 py-1.5 text-orange-300 inline-flex items-center gap-1"><SealWarning size={12} /> Provisional — some information is incomplete</span>}
            <span className="text-xs border border-exodus-border px-3 py-1.5 text-exodus-muted">{r.tier === "deep" ? "Deeper assessment" : "Quick assessment"}</span>
          </div>
          {r.confidence_label === "Low" && r.overall != null && (
            <p data-testid="report-low-confidence" className="border border-orange-300/50 bg-orange-300/5 text-sm text-orange-200 p-4 max-w-2xl">
              Low confidence: this score is based on only {Math.round(r.confidence * 100)}% of the applicable questions. Treat it as a rough starting point — answering the skipped or unknown questions will make it far more meaningful.
            </p>
          )}
          <p className="text-[11px] text-exodus-muted max-w-2xl">Scale: 85+ strong foundation · 70–84 solid · 55–69 building · 40–54 early stage · under 40 starting point. A lower score is not a judgment — it shows where attention may help most.</p>
        </div>
      </section>

      <div className="flex flex-wrap gap-3 no-print">
        <button data-testid="report-download-pdf" onClick={pdf} disabled={dl} className="btn-ghost text-xs"><FilePdf size={16} /> {dl ? "Preparing…" : "Download PDF"}</button>
        <button data-testid="report-print" onClick={() => { track("print"); window.print(); }} className="btn-ghost text-xs"><Printer size={16} /> Print</button>
        {r.tier === "quick" && <button data-testid="report-go-deeper" onClick={onDeep} className="btn-gold text-xs"><MagnifyingGlassPlus size={16} /> Go deeper (~25 more questions)</button>}
        <button data-testid="report-retake" onClick={onRetake} className="text-xs text-exodus-muted hover:text-exodus-text inline-flex items-center gap-1 px-2"><ArrowsClockwise size={14} /> Start over</button>
      </div>

      <ReportCategories categories={r.categories} narrative={data.narrative} />
      {(r.strengths.length > 0) && (
        <section data-testid="report-strengths" className="card-surface p-6 sm:p-8">
          <p className="eyebrow mb-4">What's working</p>
          <ul className="grid md:grid-cols-2 gap-3 text-sm">{r.strengths.map((s) => <li key={s} className="flex gap-2"><span className="text-exodus-emerald">✓</span>{s}</li>)}</ul>
        </section>
      )}
      <ReportTaxRetire tax={r.tax_buckets} retirement={r.retirement} />
      <ActionPlan actions={r.actions} />
      <div className="no-print"><ReportNextSteps data={data} token={token} onNeedContact={onNeedContact} /></div>
      <Disclosure />
    </div>
  );
}
